import { useCallback, useEffect, useMemo, useState } from "react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import type { RequestFilter } from "@/pages/dashboard/dashboardTab.const";
import type { InterestRequestTableRow } from "@/pages/interests/components/InterestRequestsTable";

type PreferredPartnerApiRow = {
  recommendationId: string;
  company: {
    id: string;
    gtid: string;
    headquartersCountry: string;
    industryCategory: string[];
    status: string;
  };
};

function toSuggestedTableRows(partners: PreferredPartnerApiRow[]): InterestRequestTableRow[] {
  return partners.map((item) => ({
    rowKind: "suggested" as const,
    listSource: "suggested" as const,
    matchId: item.recommendationId,
    company: {
      id: item.company.id,
      gtid: item.company.gtid,
      headquartersCountry: item.company.headquartersCountry,
      industryCategory: item.company.industryCategory,
      status: item.company.status,
    },
    collaborationStatus: "SUGGESTED",
    isRequester: false,
    requesterPaid: false,
    receiverPaid: false,
    requesterSignedNDA: false,
    receiverSignedNDA: false,
    requesterRole: null,
    expectedPartnerRole: null,
  }));
}

type UsePreferredPartnersOptions = {
  activeRequestFilter?: RequestFilter | null;
};

export function usePreferredPartners(options: UsePreferredPartnersOptions = {}) {
  const { activeRequestFilter = null } = options;

  const [apiRows, setApiRows] = useState<PreferredPartnerApiRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSuggested = useCallback(async () => {
    try {
      const res = await trpc.collaboration.getPreferredPartners.query();
      setApiRows((res.partners ?? []) as PreferredPartnerApiRow[]);
    } catch (err) {
      handleTrpcError(err);
      setApiRows([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      await fetchSuggested();
      if (!cancelled) setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [fetchSuggested]);

  useEffect(() => {
    if (activeRequestFilter === "suggested" || activeRequestFilter === "all") {
      void fetchSuggested();
    }
  }, [activeRequestFilter, fetchSuggested]);

  const suggestedTableRows = useMemo(() => toSuggestedTableRows(apiRows), [apiRows]);

  return {
    suggestedTableRows,
    suggestedCount: apiRows.length,
    loading,
  };
}
